import { useCallback, useMemo, useState } from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import type {
    BoardAddTarget,
    BoardCollapsedChange,
    BoardDisplayChange,
    BoardMove,
    BoardMoveBatch,
} from "@design-system/Board/Board.types";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { loadDemoTasks, type DemoTask } from "./fixtures/demoTasks";
import { generateStressTasks } from "./fixtures/generateStressTasks";
import {
    applyTaskChange,
    describePartialMove,
    type TaskBoardChange,
} from "./taskBoardActions";
import {
    addDraftTask,
    createDraftTask,
    createTaskPlaceRule,
    moveTaskBetweenNeighbours,
    moveTaskInSortedOrder,
    moveTasks,
} from "./taskBoardEdits";
import { TASK_SORT_INHERITANCE } from "./taskBoardSort";
import {
    createTaskPlacements,
    DEFAULT_TASK_BOARD_VIEW,
    parseTaskBoardView,
    placeDraft,
} from "./taskBoardView";
import { checkTaskChange, type TaskChangeRules } from "./taskBoardChangeRules";

interface UseTaskBoardStateArgs {
    todayDateOnly: string;
    isManualOrder: boolean;
    /** The move rules `updateTasks` holds every change to. */
    changeRules: TaskChangeRules;
    /** Opens a newly created draft in the drawer. */
    onDraftCreated: (draft: DemoTask) => void;
}

/**
 * The playground's tasks and `BoardViewState`, with the handlers that apply
 * board moves (changing whatever the columns and lanes show) and create
 * drafts in place.
 */
export const useTaskBoardState = ({
    todayDateOnly,
    isManualOrder,
    changeRules,
    onDraftCreated,
}: UseTaskBoardStateArgs) => {
    const [tasks, setTasks] = useState(() => loadDemoTasks(todayDateOnly));
    const [viewState, setViewState] = useState(DEFAULT_TASK_BOARD_VIEW);
    const [createdTaskIds, setCreatedTaskIds] = useState<readonly string[]>([]);
    const latestRef = useLatestRef({ tasks, rules: changeRules });
    const { columnBy, groupBy, sort } = parseTaskBoardView(viewState);
    const view = useMemo(
        () => ({ columnBy, groupBy, sort }),
        [columnBy, groupBy, sort],
    );

    // Collapsed ids belong to one axis: "none" is No assignee and No priority
    // alike, so a new axis starts with nothing collapsed.
    const applyViewState = useCallback(
        (change: BoardDisplayChange) =>
            setViewState((previous) => {
                const next = { ...previous, ...change };
                return {
                    ...next,
                    collapsedColumnIds:
                        next.columnBy === previous.columnBy
                            ? previous.collapsedColumnIds
                            : [],
                    collapsedLaneIds:
                        next.groupBy === previous.groupBy
                            ? previous.collapsedLaneIds
                            : [],
                };
            }),
        [],
    );
    const setCollapsed = useCallback(
        (collapsed: BoardCollapsedChange) =>
            setViewState((previous) => ({ ...previous, ...collapsed })),
        [],
    );

    const loadTasks = useCallback(
        (isStressMode: boolean) =>
            setTasks(
                isStressMode
                    ? generateStressTasks(todayDateOnly)
                    : loadDemoTasks(todayDateOnly),
            ),
        [todayDateOnly],
    );
    const getPlacements = useMemo(() => createTaskPlacements(view), [view]);

    const moveTask = isManualOrder
        ? moveTaskBetweenNeighbours
        : moveTaskInSortedOrder;
    const applyMove = useCallback(
        (move: BoardMove<DemoTask>) =>
            setTasks((previousTasks) =>
                moveTask({ tasks: previousTasks, move, view, todayDateOnly }),
            ),
        [view, moveTask, todayDateOnly],
    );
    const { toast } = useToaster();
    const applyMoves = useCallback(
        (batch: BoardMoveBatch<DemoTask>) => {
            if (batch.moves.length > 0) {
                setTasks((previousTasks) =>
                    moveTasks({
                        tasks: previousTasks,
                        moves: batch.moves,
                        moveTask,
                        view,
                        todayDateOnly,
                    }),
                );
            }
            if (batch.refusals.length > 0) {
                toast(describePartialMove(batch));
            }
        },
        [view, moveTask, todayDateOnly, toast],
    );
    const canPlaceAt = useMemo(
        () => createTaskPlaceRule({ view, todayDateOnly }),
        [view, todayDateOnly],
    );

    const updateTasks = useCallback(
        (taskIds: ReadonlySet<string>, change: TaskBoardChange) => {
            const outcome = checkTaskChange({
                ...latestRef.current,
                taskIds,
                change,
                getPlacements,
            });
            setTasks((previousTasks) =>
                applyTaskChange({
                    tasks: previousTasks,
                    taskIds: outcome.updatedIds,
                    change,
                }),
            );
            return outcome;
        },
        [latestRef, getPlacements],
    );

    const inheritSortKey = isManualOrder
        ? undefined
        : (TASK_SORT_INHERITANCE[sort] ?? undefined);
    const addTask = useCallback(
        (target: BoardAddTarget) => {
            const draft = placeDraft(
                createDraftTask(todayDateOnly),
                target.placement,
                view,
            );
            setTasks((previousTasks) =>
                addDraftTask(previousTasks, { draft, target, inheritSortKey }),
            );
            setCreatedTaskIds((previousIds) => [...previousIds, draft.id]);
            onDraftCreated(draft);
        },
        [todayDateOnly, view, inheritSortKey, onDraftCreated],
    );

    return {
        tasks,
        /** Drafts created this session, for the board's entrance animation. */
        createdTaskIds,
        view,
        viewState,
        setViewState: applyViewState,
        setCollapsed,
        getPlacements,
        applyMove,
        /** Applies a group drop in one update; toasts the tasks left behind. */
        applyMoves,
        /** Applies one change from the action set to the given tasks. */
        updateTasks,
        addTask,
        /** A gap `+` draft or a held drop lands in its gap: manual order, or a sort it can copy. */
        canPlaceBetween: isManualOrder || inheritSortKey !== undefined,
        /** Needs attention: a held drop only goes beside a task of its group. */
        canPlaceAt,
        loadTasks,
    };
};
